import type { Banner, RsuiteSortType } from '@@partner:types:app'
import type { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'

import React from 'react'
import { formatDate } from '@@partner:helpers/util'

import { Link } from 'react-router-dom'
import { Button, Tag } from 'rsuite'
import CommonStatusLabel from '@components/common/CommonStatusLabel'
import ResponsiveTable from '@components/common/ResponsiveTable/ResponsiveTable'

import { NO_PHOTO_IMAGE } from '@@partner:config/image'

export interface BannersTableProps {
  items?: Banner[]
  loading?: boolean
  sortColumn?: string
  sortType?: RsuiteSortType
  onSortColumn?: (dataKey: string, sortType?: RsuiteSortType) => void
  generateEditUrl?: (id: string | number) => string
}

const renderDateRange = (banner: Banner) => {
  const { activeFrom, activeUntil } = banner

  if (activeFrom && activeUntil) {
    return (
      <>
        <Tag>{formatDate(new Date(activeFrom))}</Tag>
        <span> / </span>
        <Tag>{formatDate(new Date(activeUntil))}</Tag>
      </>
    )
  }

  if (activeFrom && !activeUntil) {
    return (
      <>
        <span>С </span>
        <Tag>{formatDate(new Date(activeFrom))}</Tag>
      </>
    )
  }

  if (!activeFrom && activeUntil) {
    return (
      <>
        <span>До </span>
        <Tag>{formatDate(new Date(activeUntil))}</Tag>
      </>
    )
  }

  return <Tag color="red">Не задан</Tag>
}

const columns: ResponsiveTableColumn<Banner>[] = [
  {
    dataKey: 'id',
    header: 'ID',
    sortable: true,
    flexGrow: 1,
    isCardBadge: true
  },
  {
    dataKey: 'bannerSlide',
    header: 'Превью',
    flexGrow: 1,
    isCardImage: true,
    render: (banner: Banner) => (
      <img
        src={banner.bannerSlide?.url ?? NO_PHOTO_IMAGE}
        alt={banner.name}
        style={{ 
          maxWidth: 120, 
          maxHeight: 80, 
          objectFit: 'contain',
          borderRadius: 4 
        }}
      />
    ),
    renderMobile: (banner: Banner) => (
      <img
        src={banner.bannerSlide?.url ?? NO_PHOTO_IMAGE}
        alt={banner.name}
      />
    )
  },
  {
    dataKey: 'name',
    header: 'Название',
    sortable: true,
    flexGrow: 2,
    isCardTitle: true,
    render: (banner: Banner) => <Tag color="blue">{banner.name}</Tag>
  },
  {
    dataKey: 'status',
    header: 'Статус',
    sortable: true,
    flexGrow: 1,
    render: (banner: Banner) => <CommonStatusLabel status={banner.status} />
  },
  {
    dataKey: 'activeFrom',
    header: 'Срок действия',
    sortable: true,
    flexGrow: 2,
    render: renderDateRange
  }
]

export default function BannersTable ({
  items = [],
  loading = false,
  sortColumn,
  sortType,
  onSortColumn,
  generateEditUrl
}: BannersTableProps) {
  const renderActions = (banner: Banner) => (
    <>
      {generateEditUrl && (
        <Button
          appearance="primary"
          color="cyan"
          as={Link}
          to={generateEditUrl(banner.id)}
          size="xs"
        >
          Изменить
        </Button>
      )}
    </>
  )

  return (
    <ResponsiveTable<Banner>
      data={items}
      columns={columns}
      loading={loading}
      rowKey="id"
      rowHeight={100}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={onSortColumn}
      renderActions={renderActions}
      emptyMessage="Баннеры не найдены"
    />
  )
}
